<script>
import appConfig from '../../../../../../shared/helpers/appConfig';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import {
  setupFacebookSdk,
  initWhatsAppEmbeddedSignup,
  createMessageHandler,
  isValidBusinessData,
} from './whatsapp/utils';

export default {
  name: 'WhatsAppEmbeddedSignup',
  components: {
    LoadingState,
  },
  data() {
    return {
      fbSdkLoaded: false,
      isProcessing: false,
      processingMessage: '',
      authCodeReceived: false,
      authCode: null,
      businessData: null,
      isAuthenticating: false,
      whatsappIconPath: '/assets/images/dashboard/channels/whatsapp.png',
      inboxName: '',
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
    }),
    benefits() {
      return [
        {
          key: 'EASY_SETUP',
          text: 'Quick and easy setup process',
        },
        {
          key: 'SECURE_AUTH',
          text: 'Secure authentication with Facebook',
        },
        {
          key: 'AUTO_CONFIG',
          text: 'Automatic configuration of your WhatsApp Business account',
        },
      ];
    },
    showLoader() {
      return this.isAuthenticating || this.isProcessing;
    },
  },
  mounted() {
    this.initialize();
  },
  beforeDestroy() {
    this.cleanupMessageListener();
  },
  methods: {
    initialize() {
      this.setupMessageListener();
    },

    setupMessageListener() {
      // Bind handleSignupMessage to ensure 'this' is preserved
      this._boundHandleSignupMessage = this.handleSignupMessage.bind(this);
      window.addEventListener('message', this._boundHandleSignupMessage);
    },

    cleanupMessageListener() {
      if (this._boundHandleSignupMessage) {
        window.removeEventListener('message', this._boundHandleSignupMessage);
      }
    },

    handleSignupMessage(event) {
      // Use the message handler utility with arrow function to preserve 'this'
      const handler = createMessageHandler(data => {
        // Safety check to ensure 'this' is available
        if (!this || typeof this.handleEmbeddedSignupData !== 'function') {
          return;
        }

        this.handleEmbeddedSignupData(data);
      });

      handler(event);
    },

    async handleEmbeddedSignupData(data) {
      if (
        data.event === 'FINISH' ||
        data.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'
      ) {
        const businessDataLocal = data.data;

        if (isValidBusinessData(businessDataLocal)) {
          this.businessData = businessDataLocal;
          if (this.authCodeReceived && this.authCode) {
            await this.completeSignupFlow(businessDataLocal);
          } else {
            this.processingMessage =
              'Waiting for authentication to complete...';
          }
        } else {
          this.handleSignupError({
            error: 'Invalid business data received from Facebook',
          });
        }
      } else if (data.event === 'CANCEL') {
        this.handleSignupCancellation();
      } else if (data.event === 'error') {
        this.handleSignupError({
          error:
            data.error_message || 'An error occurred during the signup process',
          error_id: data.error_id,
          session_id: data.session_id,
        });
      }
    },

    handleSignupError(data) {
      this.isProcessing = false;
      this.authCodeReceived = false;
      this.isAuthenticating = false;

      const errorMessage =
        data.error ||
        data.message ||
        'An error occurred while setting up WhatsApp channel';
      useAlert(errorMessage);
    },

    handleSignupCancellation() {
      this.isProcessing = false;
      this.authCodeReceived = false;
      this.isAuthenticating = false;
    },

    handleSignupSuccess(inboxData) {
      this.isProcessing = false;
      this.isAuthenticating = false;

      if (inboxData && inboxData.id) {
        useAlert('WhatsApp inbox has been created successfully!');
        this.$store.dispatch('onboarding/completeLocalTask', 'create_inbox');
        this.$router.replace({
          name: 'settings_inboxes_choose_platform',
          params: {
            page: 'new',
            inbox_id: inboxData.id,
          },
        });
      } else {
        useAlert('WhatsApp setup completed successfully');
        this.$router.replace({
          name: 'settings_inbox_list',
        });
      }
    },

    async completeSignupFlow(businessDataParam) {
      if (!this.authCodeReceived || !this.authCode) {
        this.handleSignupError({
          error: 'Authentication was not completed properly',
        });
        return;
      }

      this.isProcessing = true;
      this.processingMessage = 'Completing WhatsApp setup...';

      try {
        const params = {
          name:
            this.inboxName ||
            `WhatsApp - ${businessDataParam?.phone_number_id}`,
          channel: {
            type: 'whatsapp',
            provider: 'whatsapp_cloud',
            provider_config: {
              code: this.authCode,
              phone_number_id: businessDataParam?.phone_number_id || '',
              business_account_id:
                businessDataParam.waba_id || businessDataParam.business_id,
            },
          },
        };

        const responseData = await this.$store.dispatch(
          'inboxes/createChannel',
          params
        );

        this.authCode = null;
        this.handleSignupSuccess(responseData);
      } catch (error) {
        const errorMessage =
          parseAPIErrorResponse(error) ||
          'An error occurred while setting up WhatsApp channel';
        this.handleSignupError({ error: errorMessage });
      }
    },

    async launchEmbeddedSignup() {
      try {
        if (!appConfig('VUE_APP_WHATSAPP_APP_ID')) {
          throw new Error('WhatsApp App ID is not configured');
        }

        this.isAuthenticating = true;
        this.processingMessage = 'Connecting to Facebook...';

        // Initialize Facebook SDK
        const FB = await setupFacebookSdk(
          appConfig('VUE_APP_WHATSAPP_APP_ID'),
          window.chatwootConfig?.whatsappApiVersion
        );

        // Ensure FB is available
        if (!FB) {
          throw new Error('Failed to initialize Facebook SDK');
        }

        this.fbSdkLoaded = true;

        const code = await initWhatsAppEmbeddedSignup(
          appConfig('VUE_APP_WHATSAPP_APP_CONFIG_ID')
        );

        this.authCode = code;
        this.authCodeReceived = true;
        this.processingMessage = 'Waiting for business information...';

        if (this.businessData) {
          this.completeSignupFlow(this.businessData);
        }
      } catch (error) {
        if (error.message === 'Login cancelled') {
          this.isProcessing = false;
          this.isAuthenticating = false;
          useAlert('WhatsApp signup was cancelled');
        } else {
          this.handleSignupError({
            error: error.message || 'Failed to load Facebook SDK',
          });
        }
      }
    },
  },
};
</script>

<template>
  <div
    class="border border-slate-25 dark:border-slate-800/60 bg-white dark:bg-slate-900 h-full p-6 w-full max-w-full md:w-3/4 md:max-w-[75%] flex-shrink-0 flex-grow-0"
  >
    <LoadingState v-if="showLoader" :message="processingMessage" />

    <div v-else>
      <div class="flex flex-col items-start mb-6 text-start">
        <div class="flex justify-start mb-6">
          <div
            class="flex justify-center items-center w-12 h-12 rounded-full bg-n-alpha-2"
          >
            <img
              :src="whatsappIconPath"
              alt="WhatsApp Business"
              class="object-contain w-8 h-8"
              draggable="false"
            />
          </div>
        </div>

        <h3 class="mb-2 text-base font-medium text-n-slate-12">
          Set up WhatsApp Business Account
        </h3>
        <p class="text-sm leading-[24px] text-n-slate-12">
          Connect your WhatsApp Business account through Facebook's embedded
          signup flow
        </p>
      </div>

      <div class="w-full mb-6">
        <label class="block mb-2">
          <span class="text-sm font-medium text-n-slate-12">
            Inbox Name (Optional)
          </span>
          <input
            v-model.trim="inboxName"
            type="text"
            class="w-full mt-1"
            placeholder="e.g., Customer Support WhatsApp"
            data-testid="whatsapp_inbox_name_input"
          />
        </label>
        <p class="text-xs text-n-slate-11 mt-1">
          If not provided, the inbox will be named using the phone number
        </p>
      </div>

      <div class="flex flex-col gap-2 mb-6">
        <div
          v-for="benefit in benefits"
          :key="benefit.key"
          class="flex gap-2 items-center text-sm text-n-slate-11"
        >
          <fluent-icon icon="search-check" class="text-n-slate-11 size-4" />
          {{ benefit.text }}
        </div>
      </div>

      <div class="flex flex-col gap-2 mb-6">
        <span class="text-sm text-n-slate-11">
          Learn more about WhatsApp Business API
          {{ ' ' }}
          <a
            href="https://developers.facebook.com/docs/whatsapp/embedded-signup/"
            target="_blank"
            rel="noopener noreferrer"
            class="underline text-primary"
          >
            here
          </a>
        </span>
      </div>

      <div class="flex mt-4">
        <woot-button
          :disabled="isAuthenticating"
          :is-loading="isAuthenticating"
          color-scheme="secondary"
          class="w-full"
          data-testid="whatsapp_connect_button"
          @click="launchEmbeddedSignup"
        >
          Connect WhatsApp Business Account
        </woot-button>
      </div>
    </div>
  </div>
</template>
